import { Button, Collapse, Empty, Modal, Spin } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import ProjectMLModelsVisualStore from '../stores/ProjectMLModelsVisualStore';

type Props = {
    store: ProjectMLModelsVisualStore;
};

const MlModelUsageModal: React.FC<Props> = ({ store }) => {
    const handleClose = () => {
        store.setIsModelRulesDialogVisible(false);
    };

    const getContent = () => {
        if (store.isLoadingModelUsage) {
            return <Spin />;
        }

        const hasRules = store.currentRulesGroups.length > 0;
        const hasTrainingProjects = store.currentModelTrainingProjects.length > 0;
        const hasProjectSettingsUsage = store.currentModelProjectSettingsUsage.length > 0;
        const hasIotaApplicationUsage = store.currentModelIotaApplicationUsage.length > 0;

        if (!hasRules && !hasTrainingProjects && !hasProjectSettingsUsage && !hasIotaApplicationUsage) {
            return <Empty description="No usage of this model was found" />;
        }

        const ruleDefaultActiveKeys = store.currentRulesGroups.map(r => r.id);
        const hasPreviousUsage = hasRules || hasTrainingProjects;

        return (
            <>
                {hasRules && (
                    <>
                        <h3>Rules</h3>
                        <Collapse defaultActiveKey={ruleDefaultActiveKeys}>
                            {store.currentRulesGroups.map(group => {
                                const tagDefaultActiveKeys = store.currentRuleTags.map(t => t.id);
                                return (
                                    <Collapse.Panel header={group.name} key={group.id}>
                                        <Collapse defaultActiveKey={tagDefaultActiveKeys}>
                                            {store.currentRuleTags
                                                .filter(tag => tag.groupId === group.id)
                                                .map(tag => (
                                                    <Collapse.Panel header={tag.name} key={tag.id}>
                                                        {store.currentModelRules
                                                            .filter(rule => rule.tagId === tag.id)
                                                            .map(rule => (
                                                                <div key={rule.id}>
                                                                    <a
                                                                        target="_blank"
                                                                        rel="noreferrer"
                                                                        href={`rules/edit/${rule.id}/false`}
                                                                    >
                                                                        {rule.name}
                                                                    </a>
                                                                </div>
                                                            ))}
                                                    </Collapse.Panel>
                                                ))}
                                        </Collapse>
                                    </Collapse.Panel>
                                );
                            })}
                        </Collapse>
                    </>
                )}
                {hasTrainingProjects && (
                    <>
                        <h3 style={{ marginTop: hasRules ? 24 : 0 }}>Training Projects</h3>
                        <div style={{ paddingLeft: 16 }}>
                            {store.currentModelTrainingProjects.map(tp => (
                                <div key={`${tp.id}-${tp.modelRole}`} style={{ marginBottom: 8 }}>
                                    <a
                                        target="_blank"
                                        rel="noreferrer"
                                        href={`/projects/${store.currentProject?.id}/training-projects/${tp.id}/dashboard`}
                                    >
                                        {tp.name}
                                    </a>{' '}
                                    <span style={{ color: '#888' }}>({tp.modelRole})</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                {hasProjectSettingsUsage && (
                    <>
                        <h3 style={{ marginTop: hasPreviousUsage ? 24 : 0 }}>Project Settings</h3>
                        <div style={{ paddingLeft: 16 }}>
                            {store.currentModelProjectSettingsUsage.map((usage, index) => (
                                <div
                                    key={`${usage.projectId}-${usage.usageType}-${usage.label}-${index}`}
                                    style={{ marginBottom: 8 }}
                                >
                                    <a target="_blank" rel="noreferrer" href={`/projects/${usage.projectId}/settings`}>
                                        {usage.label}
                                    </a>{' '}
                                    <span style={{ color: '#888' }}>({usage.projectName})</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
                {hasIotaApplicationUsage && (
                    <>
                        <h3 style={{ marginTop: hasPreviousUsage || hasProjectSettingsUsage ? 24 : 0 }}>
                            IOTA Applications
                        </h3>
                        <div style={{ paddingLeft: 16 }}>
                            {store.currentModelIotaApplicationUsage.map(usage => (
                                <div
                                    key={`${usage.projectId}-${usage.applicationDefinitionId}-${usage.settingKey}`}
                                    style={{ marginBottom: 8 }}
                                >
                                    <a
                                        target="_blank"
                                        rel="noreferrer"
                                        href={`/projects/${usage.projectId}/applicationDefinitions?highlightAppId=${usage.applicationDefinitionId}`}
                                    >
                                        {usage.applicationDefinitionName}
                                    </a>{' '}
                                    <span style={{ color: '#888' }}>({usage.settingName ?? usage.settingKey})</span>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </>
        );
    };

    return (
        <Modal
            title={`Model Usage: ${store.currentModel?.name}`}
            visible={store.isModelRulesDialogVisible}
            className="alpha-portal-dialog two-columns"
            closable={false}
            maskClosable={false}
            destroyOnClose
            width={600}
            centered
            footer={[
                <Button
                    key="mlmodel-rules-dialog-close"
                    data-id="mlmodel-rules-dialog-close"
                    className="light"
                    size="large"
                    onClick={handleClose}
                >
                    Close
                </Button>
            ]}
            onCancel={handleClose}
        >
            {getContent()}
        </Modal>
    );
};

export default observer(MlModelUsageModal);
